import {
  markDragGestureClickSuppression,
  markPointerDownSelection,
} from '@components/Viewer3D/pointerSelectionGuard'
import {
  beginFloorDragSession,
  computeCameraAlignedScreenDelta,
  computeFloorDragPosition,
  isSaneFloorDragPosition,
  type FloorDragSession,
} from '@utils/camera/planarFloorDrag'
import { ThreeEvent, useThree } from '@react-three/fiber'
import { MODULAR_FLOOR_DRAG_MIN_PX } from '@utils/modularSnap/config'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { useAppStore } from '@store'
import { useCallback, useEffect, useRef } from 'react'
import type { Group, Matrix4 as Matrix4Type, PerspectiveCamera } from 'three'
import { Matrix4, Quaternion, Vector3 } from 'three'

type DragHandler = (l: Matrix4, dl: Matrix4, w: Matrix4, dw: Matrix4) => void

type Options = {
  enabled: boolean
  id: string
  matrix: Matrix4Type
  isBlocked?: boolean
  isModalOpen?: boolean
  isSelected?: boolean
  matrixRootRef: React.RefObject<Group>
  onDragStart: () => void
  /** Resuelve colisión/snap en vivo; devuelve null si el movimiento se rechaza (penetración). */
  onDragLive: (proposedMatrix: Matrix4) => Matrix4 | null
  onDragCommit: DragHandler
  onDragEnd: () => void
  onPointerDownSelect: (e: ThreeEvent<PointerEvent>) => void
}

export function useMeshFloorDrag3D({
  enabled,
  id,
  matrix,
  isBlocked = false,
  isModalOpen = false,
  isSelected = true,
  matrixRootRef,
  onDragStart,
  onDragLive,
  onDragCommit,
  onDragEnd,
  onPointerDownSelect,
}: Options) {
  const { camera, raycaster, gl, invalidate } = useThree()

  const isPointerDownRef = useRef(false)
  const isDraggingRef = useRef(false)
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const lastClientRef = useRef<{ x: number; y: number } | null>(null)
  const lastAppliedClientRef = useRef<{ x: number; y: number } | null>(null)
  const hasMovedRef = useRef(false)
  const dragActiveRef = useRef(false)
  const gestureItemRef = useRef<string | null>(null)

  const dragSessionRef = useRef<FloorDragSession | null>(null)
  const currentDragPositionRef = useRef<Vector3 | null>(null)
  const committedMatrixRef = useRef<Matrix4 | null>(null)

  const onDragEndRef = useRef(onDragEnd)
  const onDragCommitRef = useRef(onDragCommit)
  const onDragLiveRef = useRef(onDragLive)

  const scratchNext = useRef(new Vector3())
  const scratchDelta = useRef(new Vector3())
  const scratchRot = useRef(new Quaternion())
  const scratchScale = useRef(new Vector3())
  const scratchMatrix = useRef(new Matrix4())
  const scratchDeltaMatrix = useRef(new Matrix4())
  const scratchInverse = useRef(new Matrix4())

  onDragEndRef.current = onDragEnd
  onDragCommitRef.current = onDragCommit
  onDragLiveRef.current = onDragLive

  const canInteract = enabled && !isBlocked && !isModalOpen

  const armPointerGesture = () => {
    useModularUiStore.getState().setItemDragActive(true, id)
  }

  const releasePointerGesture = () => {
    useModularUiStore.getState().setItemDragActive(false, null)
    useModularUiStore.getState().setLiveDrag(null)
  }

  const resetDragState = () => {
    isPointerDownRef.current = false
    isDraggingRef.current = false
    dragActiveRef.current = false
    gestureItemRef.current = null
    pointerStartRef.current = null
    lastClientRef.current = null
    lastAppliedClientRef.current = null
    dragSessionRef.current = null
    currentDragPositionRef.current = null
    committedMatrixRef.current = null
  }

  const resolveDragStartMatrix = (): Matrix4 => {
    const matrixRoot = matrixRootRef.current
    if (matrixRoot?.matrix) {
      return matrixRoot.matrix.clone()
    }
    return (
      (useAppStore.getState().items.get(String(id))?.matrix as Matrix4 | undefined)?.clone() ??
      matrix.clone()
    )
  }

  const composeDragMatrix = (position: Vector3): Matrix4 => {
    const session = dragSessionRef.current
    if (!session) return scratchMatrix.current

    session.initialMatrix.decompose(new Vector3(), scratchRot.current, scratchScale.current)
    scratchMatrix.current.compose(position, scratchRot.current, scratchScale.current)
    return scratchMatrix.current
  }

  const publishLiveDrag = (position: Vector3) => {
    useModularUiStore.getState().setLiveDrag({
      itemId: id,
      x: position.x,
      y: position.y,
      z: position.z,
    })
  }

  const applyVisualMatrix = (nextMatrix: Matrix4) => {
    const matrixRoot = matrixRootRef.current
    if (!matrixRoot) return
    matrixRoot.matrix.copy(nextMatrix)
    matrixRoot.matrixAutoUpdate = false
    matrixRoot.updateMatrixWorld(true)
    if (!committedMatrixRef.current) {
      committedMatrixRef.current = new Matrix4()
    }
    committedMatrixRef.current.copy(nextMatrix)
    invalidate()
  }

  const applyDragAtClient = (clientX: number, clientY: number): boolean => {
    const session = dragSessionRef.current
    const currentPosition = currentDragPositionRef.current
    const lastApplied = lastAppliedClientRef.current
    if (!session || !currentPosition || !lastApplied) return false

    const perspCamera = camera as PerspectiveCamera
    let moved = false

    const absolute = computeFloorDragPosition(
      session,
      raycaster,
      perspCamera,
      gl.domElement,
      clientX,
      clientY,
      null,
      scratchNext.current,
    )

    if (absolute) {
      moved = true
    } else if (clientX !== lastApplied.x || clientY !== lastApplied.y) {
      computeCameraAlignedScreenDelta(
        perspCamera,
        gl.domElement,
        lastApplied,
        { x: clientX, y: clientY },
        session.planeY,
        currentPosition,
        scratchDelta.current,
      )
      scratchNext.current.copy(currentPosition).add(scratchDelta.current)
      if (isSaneFloorDragPosition(scratchNext.current)) {
        moved = true
      }
    }

    if (!moved) return false

    const nextMatrix = composeDragMatrix(scratchNext.current)
    const resolvedMatrix = onDragLiveRef.current(nextMatrix.clone())
    if (!resolvedMatrix) return false

    lastAppliedClientRef.current = { x: clientX, y: clientY }
    resolvedMatrix.decompose(scratchNext.current, new Quaternion(), new Vector3())
    currentDragPositionRef.current.copy(scratchNext.current)

    applyVisualMatrix(resolvedMatrix)
    publishLiveDrag(scratchNext.current)
    return true
  }

  const finishDrag = useCallback(() => {
    if (!isPointerDownRef.current && !isDraggingRef.current && !dragActiveRef.current) return

    const wasDragging = dragActiveRef.current || isDraggingRef.current
    const session = dragSessionRef.current
    const client = lastClientRef.current

    if (wasDragging && session && client) {
      applyDragAtClient(client.x, client.y)

      const finalMatrix = (
        committedMatrixRef.current ?? composeDragMatrix(currentDragPositionRef.current!)
      ).clone()
      scratchInverse.current.copy(session.initialMatrix).invert()
      scratchDeltaMatrix.current.copy(finalMatrix).multiply(scratchInverse.current)

      onDragCommitRef.current(
        finalMatrix,
        scratchDeltaMatrix.current.clone(),
        finalMatrix,
        scratchDeltaMatrix.current.clone(),
      )
    }

    resetDragState()
    document.body.style.cursor = ''

    if (wasDragging) {
      markDragGestureClickSuppression()
      onDragEndRef.current()
    }
    releasePointerGesture()
  }, [camera, gl.domElement, id, invalidate, matrixRootRef, raycaster])

  const cancelActiveDrag = useCallback(() => {
    const ui = useModularUiStore.getState()
    const ownsGesture =
      gestureItemRef.current === String(id) ||
      ui.draggingItemId === id ||
      ui.liveDrag?.itemId === id

    if (!ownsGesture) return

    if (isDraggingRef.current || dragActiveRef.current || isPointerDownRef.current) {
      finishDrag()
      return
    }

    if (ui.liveDrag?.itemId === id) {
      useModularUiStore.getState().setLiveDrag(null)
    }
    if (ui.draggingItemId === id) {
      releasePointerGesture()
    }
  }, [finishDrag, id])

  const beginDrag = (clientX: number, clientY: number) => {
    if (isDraggingRef.current || gestureItemRef.current !== String(id)) return

    const storeMatrix = resolveDragStartMatrix()
    const currentPosition = new Vector3()
    storeMatrix.decompose(currentPosition, new Quaternion(), new Vector3())

    const perspCamera = camera as PerspectiveCamera
    const session = beginFloorDragSession(
      raycaster,
      perspCamera,
      gl.domElement,
      clientX,
      clientY,
      currentPosition,
      storeMatrix,
    )
    if (!session) return

    dragSessionRef.current = session
    currentDragPositionRef.current = currentPosition.clone()
    lastClientRef.current = { x: clientX, y: clientY }
    lastAppliedClientRef.current = { x: clientX, y: clientY }

    isDraggingRef.current = true
    dragActiveRef.current = true
    hasMovedRef.current = true
    document.body.style.cursor = 'grabbing'
    armPointerGesture()
    onDragStart()
    applyDragAtClient(clientX, clientY)
  }

  const recordPointerClient = (e: PointerEvent) => {
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e]
    for (const event of events) {
      lastClientRef.current = { x: event.clientX, y: event.clientY }
      if (isDraggingRef.current) {
        applyDragAtClient(event.clientX, event.clientY)
      }
    }
  }

  const tryBeginDragFromPointer = (clientX: number, clientY: number) => {
    if (!isPointerDownRef.current || !pointerStartRef.current || isDraggingRef.current) return
    if (gestureItemRef.current !== String(id)) return

    const px = Math.hypot(clientX - pointerStartRef.current.x, clientY - pointerStartRef.current.y)
    if (px >= MODULAR_FLOOR_DRAG_MIN_PX) {
      beginDrag(clientX, clientY)
    }
  }

  useEffect(() => {
    if (!canInteract) return

    const handlePointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (gestureItemRef.current !== String(id)) return
      finishDrag()
    }

    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (gestureItemRef.current !== String(id)) return
      if (!isPointerDownRef.current) return
      if ((e.buttons & 1) === 0) {
        finishDrag()
        return
      }
      recordPointerClient(e)
      if (!isDraggingRef.current && lastClientRef.current) {
        tryBeginDragFromPointer(lastClientRef.current.x, lastClientRef.current.y)
      }
    }

    window.addEventListener('pointerup', handlePointerUp, true)
    window.addEventListener('pointercancel', handlePointerUp, true)
    window.addEventListener('pointermove', handleGlobalPointerMove, true)
    window.addEventListener('blur', finishDrag)

    return () => {
      window.removeEventListener('pointerup', handlePointerUp, true)
      window.removeEventListener('pointercancel', handlePointerUp, true)
      window.removeEventListener('pointermove', handleGlobalPointerMove, true)
      window.removeEventListener('blur', finishDrag)
    }
  }, [canInteract, finishDrag, id])

  useEffect(() => {
    if (!canInteract || isSelected) return
    cancelActiveDrag()
  }, [canInteract, cancelActiveDrag, isSelected])

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (!canInteract || e.button !== 0) return
    e.stopPropagation()

    resetDragState()
    hasMovedRef.current = false
    isPointerDownRef.current = true
    gestureItemRef.current = String(id)
    pointerStartRef.current = { x: e.clientX, y: e.clientY }
    lastClientRef.current = { x: e.clientX, y: e.clientY }

    markPointerDownSelection(id)
    armPointerGesture()
    onPointerDownSelect(e)
  }

  const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
    if (!canInteract || gestureItemRef.current !== String(id)) return
    if (!isPointerDownRef.current) return
    if ((e.buttons & 1) === 0) {
      finishDrag()
      return
    }

    e.stopPropagation()
    recordPointerClient(e.nativeEvent)
    if (!isDraggingRef.current && lastClientRef.current) {
      tryBeginDragFromPointer(lastClientRef.current.x, lastClientRef.current.y)
    }
  }

  const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
    if (gestureItemRef.current !== String(id)) return
    if (!isPointerDownRef.current && !isDraggingRef.current) return
    if (hasMovedRef.current) e.stopPropagation()
    finishDrag()
  }

  return {
    meshPointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
    suppressClick: () => hasMovedRef.current,
    cancelActiveDrag,
  }
}
